import * as React from "react";
import Marquee from "react-marquee-slider";
import styles from "./styles.module.scss";
import { MemoMessageModal as MessageModal } from "../../modals/MessageModal";
import Dialog from "../../Dialog";
import { useQuery } from "@apollo/client";
import {
  EventBySlugQuery,
  GetApprovedMessagesQuery,
  GetApprovedMessagesQueryVariables,
} from "@src/@types/api.d";
import { GetApprovedMessages } from "@src/graphql/queries";
import { getCookie } from "cookies-next";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
// import image from "../../../../public/images/t10/bg-icons-1.png";

type Props = {
  eventId: number;
  slugData: EventBySlugQuery | undefined;
};

const MarqueeComp: React.FC<Props> = ({ slugData, eventId }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const [open, setOpen] = React.useState<boolean>(false);
  const [show, setShow] = React.useState<boolean>(false);
  const currentAccessToken = getCookie("tsl") as string;
  const [isMarqueePaused, setIsMarqueePaused] = React.useState(false);

  const pauseMarquee = () => setIsMarqueePaused(true);
  const resumeMarquee = () => setIsMarqueePaused(false);

  React.useEffect(() => {
    setShow(true);
  }, []);

  const { data: getApprovedMessagesData, loading: messagesLoading } = useQuery<
    GetApprovedMessagesQuery,
    GetApprovedMessagesQueryVariables
  >(GetApprovedMessages, {
    skip: !eventId,
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    variables: { eventId },
    pollInterval: 1000 * 60,
  });

  // Use only API messages, no hardcoded fallback
  const messagesToDisplay =
    getApprovedMessagesData?.getApprovedMessages &&
    getApprovedMessagesData.getApprovedMessages.length > 0
      ? getApprovedMessagesData.getApprovedMessages
      : [];

  // Defer Marquee render until after layout so react-marquee-slider gets correct dimensions.
  // Fixes messages not showing on first load / after refresh (library init race).
  const [marqueeReady, setMarqueeReady] = React.useState(false);
  const hasMessages = messagesToDisplay.length > 0;
  React.useEffect(() => {
    if (!hasMessages) {
      setMarqueeReady(false);
      return;
    }
    const id = requestAnimationFrame(() => {
      setMarqueeReady(true);
    });
    return () => cancelAnimationFrame(id);
  }, [hasMessages]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 50,
      scale: 0.9,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.25, 0.25, 0.75],
      },
    },
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 30,
      rotateX: 15,
    },
    visible: {
      opacity: 1,
      y: 0,
      rotateX: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const buttonVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "backOut",
      },
    },
    hover: {
      scale: 1.05,
      boxShadow: "0 8px 25px rgba(255, 255, 255, 0.3)",
      transition: {
        duration: 0.2,
      },
    },
    tap: {
      scale: 0.95,
    },
  };

  // Don't render section at all only when we're done loading and have no messages
  if (
    !messagesLoading &&
    (!messagesToDisplay || messagesToDisplay.length === 0)
  )
    return null;

  return (
    <motion.div
      ref={ref}
      className="bg-[#3E4F3C] flex flex-col lg:flex-row items-center lg:justify-between gap-8 py-10 lg:py-12 lg:px-10 min-h-[100vh]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      {/* Heading & thank you note */}
      <motion.div
        className="text-[#F9F6EC] w-[90%] md:w-[80%] lg:w-[50%] flex flex-col items-center justify-center"
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        <motion.div
          className="border-t border-[#F9F6EC] flex flex-col py-12 gap-6 w-full items-center"
          variants={cardVariants}
        >
          <motion.h3
            className="font-corGaramond text-[#F9F6EC] text-5xl lg:text-8xl italic text-center font-bold tracking-wide uppercase"
            variants={itemVariants}
            whileHover={{
              scale: 1.05,
              textShadow: "0 0 20px rgba(255, 255, 255, 0.5)",
              transition: { duration: 0.3 },
            }}
          >
            Special Messages
          </motion.h3>

          <motion.p
            className="font-corGaramond text-xl lg:text-2xl leading-relaxed text-center max-w-md"
            variants={itemVariants}
          >
            Your heartfelt messages and warm wishes make our celebration even
            more meaningful. Thank you for sharing in our joy.
          </motion.p>

          {/* Button */}
          {show && !!currentAccessToken && (
            <Dialog
              trigger={
                <motion.button
                  className="px-6 py-3 border border-[#F9F6EC] bg-transparent font-temp text-sm lg:text-base 
                           uppercase tracking-wide transition-colors duration-300 relative overflow-hidden group rounded-xl"
                  type="button"
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <motion.div
                    className="absolute inset-0 bg-[#F9F6EC]"
                    initial={{ x: "-100%" }}
                    whileHover={{ x: 0 }}
                    transition={{ duration: 0.3 }}
                  />
                  <span className="relative z-10 !text-[#F9F6EC] group-hover:text-[#3E4F3C] transition-colors duration-300">
                    Drop a Special Note
                  </span>
                </motion.button>
              }
              open={open}
              onOpenChange={setOpen}
            >
              <MessageModal
                name={slugData?.eventBySlug?.name ?? ""}
                eventId={eventId}
                setOpen={setOpen}
              />
            </Dialog>
          )}
        </motion.div>

        <motion.div
          className="border-b border-[#F9F6EC] w-full"
          initial={{ scaleX: 0 }}
          animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        />
      </motion.div>

      {/* Right Column - Marquee */}
      <motion.div
        className="w-[100%] lg:w-[50%] min-h-[50vh] lg:min-h-[100vh] flex flex-col justify-center items-center relative"
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        <div
          className={styles["home__marquee"]}
          onMouseEnter={pauseMarquee}
          onMouseLeave={resumeMarquee}
          onClick={pauseMarquee}
          style={{ width: "100%", height: "100%" }}
        >
          {marqueeReady && (
            <>
              {/* First Marquee Row - key forces fresh mount when data is ready */}
              <div style={{ height: "250px", maxWidth: "100%", width: "100%" }}>
                <Marquee
                  key={`marquee-rtl-${messagesToDisplay.length}`}
                  velocity={isMarqueePaused ? 0 : 40}
                  direction="rtl"
                  scatterRandomly={false}
                  resetAfterTries={200}
                  onInit={() => null}
                  onFinish={() => null}
                >
                  {messagesToDisplay
                    .filter((_, i) => i % 2 === 0)
                    .map((item, index) => (
                      <div
                        key={`quotes-${index}`}
                        className={`${styles["home__special-note-box"]} mx-2`}
                      >
                        <p
                          className={`${styles["home__special-note-text"]} font-ebGaramond text-[#F9F6EC]`}
                        >
                          {item?.content ?? ""}
                        </p>
                      </div>
                    ))}
                </Marquee>
              </div>

              {/* Second Marquee Row */}
              {messagesToDisplay.length > 1 && (
                <div
                  style={{ height: "250px", maxWidth: "100%", width: "100%" }}
                >
                  <Marquee
                    key={`marquee-ltr-${messagesToDisplay.length}`}
                    velocity={isMarqueePaused ? 0 : 40}
                    direction="ltr"
                    scatterRandomly={false}
                    resetAfterTries={200}
                    onInit={() => null}
                    onFinish={() => null}
                  >
                    {messagesToDisplay
                      .filter((_, i) => i % 2 !== 0)
                      .map((item, index) => (
                        <div
                          key={`quotes-2-${index}`}
                          className={`${styles["home__special-note-box"]} mx-2`}
                        >
                          <p
                            className={`${styles["home__special-note-text"]} font-ebGaramond text-[#F9F6EC]`}
                          >
                            {item?.content ?? ""}
                          </p>
                        </div>
                      ))}
                  </Marquee>
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>

      {/* Floating Animation Elements */}
      <motion.div
        className="absolute top-10 left-10 w-2 h-2 bg-[#F9F6EC]/30 rounded-full"
        animate={{
          y: [0, -15, 0],
          opacity: [0.3, 0.8, 0.3],
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 4,
          repeat: Number.POSITIVE_INFINITY,
          ease: "easeInOut",
        }}
      />

      <motion.div
        className="absolute bottom-20 right-20 w-3 h-3 bg-[#EAE4CC]/20 rounded-full"
        animate={{
          y: [0, -20, 0],
          x: [0, 10, 0],
          opacity: [0.2, 0.7, 0.2],
          scale: [1, 1.3, 1],
        }}
        transition={{
          duration: 5,
          repeat: Number.POSITIVE_INFINITY,
          ease: "easeInOut",
          delay: 2,
        }}
      />

      <motion.div
        className="absolute top-1/2 left-1/4 w-1 h-1 bg-[#EAE4CC]/40 rounded-full"
        animate={{
          rotate: [0, 360],
          scale: [1, 1.5, 1],
          opacity: [0.4, 0.8, 0.4],
        }}
        transition={{
          duration: 6,
          repeat: Number.POSITIVE_INFINITY,
          ease: "linear",
          delay: 1,
        }}
      />
    </motion.div>
  );
};

export default MarqueeComp;
